import * as UserRoleService from '@services/UserRoleService'
import { RegionMappingService } from '@amzn/ihm-associate-tools-js-lib'
import { getCurrentRegion, getStoreId } from '@atredux/selectors/storeSelector'
import { store } from '@atredux/store'
import { LexRuntimeServiceClient } from '@aws-sdk/client-lex-runtime-service'
import { Polly } from '@aws-sdk/client-polly'

const clientCache = {}
const renewTimeStamp = {}
const bufferMinutesInMs = 15 * 60 * 1000 // 15 min

export const AwsClient = Object.freeze({
    POLLY: 'Polly',
    LEX_RUNTIME: 'LexRuntime',
})

/**
 * Service that provides AWS Clients already configured with credentials and awsRegion. Also, handles
 * caching of the clients.
 *
 * @param {string} clientName - The name of the client as it appears in the AWS SDK
 * @param {string} role - The role to assume (@see UserRoleServiceModule#userRoleService).
 * @param {string} awsRegion - Optional argument to override the current region
 * @return {Promise} awsClientPromise - A promise that will resolve to a configured AWS client or will
 *  reject if there's parameters missing or there is a failure.
 */
export const getAwsClient = (clientName, role, awsRegion) => {

    if (!clientName) {
        return Promise.reject('clientName is required')
    }

    if (!(Object.values(AwsClient).toString().includes(clientName))) {
        return Promise.reject('Client "' + clientName + '" is not available')
    }

    if (!role) {
        return Promise.reject('role is required')
    }

    const region = awsRegion || getCurrentAwsRegion()
    return getAwsClientPromise(clientName, role, region)
}

/**
 * Returns the current AWS Region
 */
const getCurrentAwsRegion = () => {
    const currentAirportCode = getCurrentRegion(store.getState())
    return RegionMappingService.getAwsRegionFromAirportCode(currentAirportCode)
}

/**
 * Generates a key that is unique for this combination of arguments. This key is used to cache
 * the client for those specific params.
 */
const generateClientKey = (clientName, role, awsRegion) => {
    return [
        clientName,
        role,
        awsRegion,
    ].join('::')
}

/**
 * Returns a cached client for the arguments if it exists. If not, assumes the role and creates a
 * client with the role credentials and awsRegion. It returns this client after adding it to the
 * cache.
 *
 * Note: We don't cache failed promises, but it is entirely possible for two callers to get the same
 *  promise (if it was in-flight) and then get the rejection (when the promise finally settles).
 *
 */
const getAwsClientPromise = (clientName, role, awsRegion) => {
    const storeId = getStoreId(store.getState())
    const clientKey = generateClientKey(clientName, role, awsRegion)
    const currentTimeStamp = Date.now()

    // Check if the client is cached and not expired. We are keeping a buffer for 15 min to avoid last minute issues.
    if (clientCache[clientKey] && currentTimeStamp < renewTimeStamp[clientKey]) {
        console.warn('returning cached credentials for client', clientName)
        return clientCache[clientKey]
    }

    // User Role service has its own caching layer which we need to bypass since the client is expired.
    const byPassCacheInUserRoleService = true

    clientCache[clientKey] = UserRoleService.assumeUserRole(storeId, role, byPassCacheInUserRoleService)
        .then((credentials) => {
            const config = {
                region: awsRegion,
                credentials: {
                    accessKeyId: credentials.accessKeyId,
                    secretAccessKey: credentials.secretAccessKey,
                    sessionToken: credentials.sessionToken,
                    expirationTimestamp: credentials.expirationTimestamp,
                },
            }
            renewTimeStamp[clientKey] = Number(credentials.expirationTimestamp) - bufferMinutesInMs // Renew 15 min before expiry
            switch (clientName) {
                case AwsClient.LEX_RUNTIME:
                    return new LexRuntimeServiceClient(config)
                case AwsClient.POLLY:
                    return new Polly(config)
                default:
                    return null
            }
        })
        .catch((error) => {
            console.error('AwsClientProviderService getAwsClientPromise failed with error', error)
            clientCache[clientKey] = null
            return Promise.reject(error)
        })

    return clientCache[clientKey]
}
